import type { ReactNode } from 'react';

import { HelpPopover } from './HelpPopover';

interface PageHeaderProps {
  /**
   * The section this page belongs to, small, above the title. Still accepted, no longer
   * used by the screens: the section is already in the navigation, and a word above the
   * title is one more thing to read before the content.
   */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** One line under the title, in the quiet ink. One line: anything longer goes in `help`. */
  description?: ReactNode;
  /** Something small next to the title: a status badge, a count. */
  badge?: ReactNode;
  actions?: ReactNode;
  /** Breadcrumb or back link, above the title. */
  back?: ReactNode;
  /**
   * The help of this screen, behind a round `?` next to the title. A list of nodes is
   * rendered as bullets (what `HelpBox` used to take as `items`); anything else as it is.
   */
  help?: ReactNode;
}

/**
 * The top of every screen: a 22/24 px title, the one primary action on the right, and
 * the help one click away. Everything that used to sit here (the section in small caps,
 * a two-line description, the help box) is gone or folded, so the content starts higher.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  badge,
  actions,
  back,
  help,
}: PageHeaderProps) {
  return (
    <header className="relative flex flex-col gap-2">
      {back ? <div className="text-[13.5px]">{back}</div> : null}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 className="text-ink font-display text-[22px] leading-7 font-semibold lg:text-[24px] lg:leading-8">
              {title}
            </h1>
            {help ? <HelpPopover content={help} /> : null}
            {badge}
          </div>
          {description ? (
            <p className="text-ink-3 mt-0.5 text-[14px] leading-snug">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}
